import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyTierBadge from './LoyaltyTierBadge'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

function LoyaltyLandingPageTierInformation() {
  const intl = useIntl()
  const [config, configFetching, configError] = useLoyaltyConfig()

  const bronzeTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'raM0e+vWUg',
        defaultMessage: 'Your journey starts here',
        description: 'Title for bronze tier loyalty information',
      }),
    [intl],
  )

  const bronzeDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'uO3VmnGUCo',
        defaultMessage: 'Start earning points and status credits from your very first booking.',
        description: 'Description for bronze tier loyalty benefits',
      }),
    [intl],
  )

  const silverTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'IFS7WdPwSJ',
        defaultMessage: 'Step up the benefits',
        description: 'Title for silver tier loyalty information',
      }),
    [intl],
  )

  const silverDescriptionWithLounge = useMemo(
    () =>
      intl.formatMessage({
        id: 'bv51dKtDYl',
        defaultMessage: 'Access added perks like lounge passes, tour credits, and discounted photoshoots.',
        description: 'Description for silver tier loyalty benefits when lounge passes are enabled',
      }),
    [intl],
  )

  const silverDescriptionWithoutLounge = useMemo(
    () =>
      intl.formatMessage({
        id: '8d/LLFD/kG',
        defaultMessage: 'Access added perks like tour credits, and discounted photoshoots.',
        description: 'Description for silver tier loyalty benefits when lounge passes are not enabled',
      }),
    [intl],
  )

  const goldTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'S5EXPp3+yL',
        defaultMessage: 'A new gold standard',
        description: 'Title for gold tier loyalty information',
      }),
    [intl],
  )

  const goldDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'HCgksMApKu',
        defaultMessage: 'Enjoy elevated benefits including free room upgrades and chauffeur airport transfers.',
        description: 'Description for gold tier loyalty benefits',
      }),
    [intl],
  )

  const platinumTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'fCNjQpzAxB',
        defaultMessage: 'The complete VIP experience',
        description: 'Title for platinum tier loyalty information',
      }),
    [intl],
  )

  const platinumDescription = useMemo(
    () =>
      intl.formatMessage({
        id: 'umlUAvsFtg',
        defaultMessage: 'Indulge in unlimited chauffeur transfers, additional room upgrades for the ultimate escape.',
        description: 'Description for platinum tier loyalty benefits',
      }),
    [intl],
  )

  if (configFetching) return <LoadingIndicator />
  if (!config || configError) return null

  return (
    <Group direction="vertical" tabletDirection="horizontal" gap={48} tabletGap={36}>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="bronze" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {bronzeTitle}
          </Heading>
          <BodyText variant="medium">
            {bronzeDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="silver" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {silverTitle}
          </Heading>
          {config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithLounge}
            </BodyText>
          )}
          {!config.regionEnabledBenefits.lounge_pass && (
            <BodyText variant="medium">
              {silverDescriptionWithoutLounge}
            </BodyText>
          )}
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="gold" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {goldTitle}
          </Heading>
          <BodyText variant="medium">
            {goldDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <VerticalSpacer gap={16}>
        <LoyaltyTierBadge size="large" tier="platinum" />
        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {platinumTitle}
          </Heading>
          <BodyText variant="medium">
            {platinumDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
    </Group>
  )
}

export default LoyaltyLandingPageTierInformation
